import React, { useState, useEffect } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  BookOpen, MessageSquare, History, Users, Scroll, BarChart3,
  Plus, Settings, FileText, Database, Save, X, Loader2, ChevronLeft, Pencil
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { adminApi, quranApi } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { GlassCard, PageLoading } from '../components/shared';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import LexicalImportCard from '../components/admin/LexicalImportCard';
import MorphologyToolsCard from '../components/admin/MorphologyToolsCard';
import GlossesToolsCard from '../components/admin/GlossesToolsCard';
import RichTextEditor from '../components/admin/RichTextEditor';

const statCards = [
  { key: 'surahs', label: 'Sourates', icon: BookOpen, color: 'text-emerald-400' },
  { key: 'verses', label: 'Versets', icon: FileText, color: 'text-emerald-400' },
  { key: 'hadiths', label: 'Hadiths', icon: MessageSquare, color: 'text-amber-400' },
  { key: 'prophets', label: 'Prophètes', icon: Scroll, color: 'text-purple-400' },
  { key: 'companions', label: 'Compagnons', icon: Users, color: 'text-blue-400' },
  { key: 'events', label: 'Événements', icon: History, color: 'text-cyan-400' },
  { key: 'users', label: 'Utilisateurs', icon: Users, color: 'text-pink-400' },
];

const periods = [
  { value: 'pre_revelation', label: 'Avant la Révélation' },
  { value: 'meccan', label: 'Période Mecquoise' },
  { value: 'hijra', label: 'Hégire' },
  { value: 'medinan', label: 'Période Médinoise' },
  { value: 'battles', label: 'Batailles' },
  { value: 'treaties', label: 'Traités' },
  { value: 'final_period', label: 'Période finale' },
];

const certaintyLevels = [
  { value: 'established', label: 'Établi' },
  { value: 'commonly_accepted', label: 'Communément accepté' },
  { value: 'debated', label: 'Débattu' },
  { value: 'weak', label: 'Faible' },
];

// Event Form Component
const EventForm = ({ onSuccess, onCancel }) => {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    title_arabic: '', title_english: '', title_french: '',
    date_hijri: '', date_gregorian: '', period: 'meccan',
    location: '', narrative: '', certainty_level: 'commonly_accepted'
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await adminApi.createEvent(form);
      toast.success('Événement créé avec succès');
      onSuccess?.();
    } catch (error) {
      toast.error('Échec de la création');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label>Titre (Anglais) *</Label>
          <Input value={form.title_english} onChange={e => setForm({...form, title_english: e.target.value})} required className="bg-surface border-border" />
        </div>
        <div>
          <Label>Titre (Arabe) *</Label>
          <Input value={form.title_arabic} onChange={e => setForm({...form, title_arabic: e.target.value})} required className="bg-surface border-border" dir="rtl" />
        </div>
        <div>
          <Label>Titre (Français)</Label>
          <Input value={form.title_french} onChange={e => setForm({...form, title_french: e.target.value})} className="bg-surface border-border" />
        </div>
        <div>
          <Label>Période *</Label>
          <Select value={form.period} onValueChange={v => setForm({...form, period: v})}>
            <SelectTrigger className="bg-surface border-border"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-surface border-border">
              {periods.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Date (Hégire)</Label>
          <Input value={form.date_hijri} onChange={e => setForm({...form, date_hijri: e.target.value})} placeholder="ex. 17 Ramadan, 2 AH" className="bg-surface border-border" />
        </div>
        <div>
          <Label>Date (Grégorienne)</Label>
          <Input value={form.date_gregorian} onChange={e => setForm({...form, date_gregorian: e.target.value})} placeholder="ex. 624 EC" className="bg-surface border-border" />
        </div>
        <div>
          <Label>Lieu</Label>
          <Input value={form.location} onChange={e => setForm({...form, location: e.target.value})} className="bg-surface border-border" />
        </div>
        <div>
          <Label>Niveau de certitude</Label>
          <Select value={form.certainty_level} onValueChange={v => setForm({...form, certainty_level: v})}>
            <SelectTrigger className="bg-surface border-border"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-surface border-border">
              {certaintyLevels.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <Label>Récit *</Label>
        <Textarea value={form.narrative} onChange={e => setForm({...form, narrative: e.target.value})} required rows={5} className="bg-surface border-border" />
      </div>
      <div className="flex gap-2 justify-end">
        <Button type="button" variant="outline" onClick={onCancel} className="border-border">Annuler</Button>
        <Button type="submit" disabled={loading} className="bg-primary text-background">
          {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          Enregistrer l'événement
        </Button>
      </div>
    </form>
  );
};

// Companion Form Component
const CompanionForm = ({ onSuccess, onCancel }) => {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name_arabic: '', name_english: '', name_french: '', nickname: '',
    birth_year: '', death_year: '', biography: '', relationship_to_prophet: '',
    notable_achievements: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = {
        ...form,
        notable_achievements: form.notable_achievements.split('\n').filter(a => a.trim()),
        sources: []
      };
      await adminApi.createCompanion(data);
      toast.success('Compagnon créé avec succès');
      onSuccess?.();
    } catch (error) {
      toast.error('Échec de la création');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label>Nom (Anglais) *</Label>
          <Input value={form.name_english} onChange={e => setForm({...form, name_english: e.target.value})} required className="bg-surface border-border" />
        </div>
        <div>
          <Label>Nom (Arabe) *</Label>
          <Input value={form.name_arabic} onChange={e => setForm({...form, name_arabic: e.target.value})} required className="bg-surface border-border" dir="rtl" />
        </div>
        <div>
          <Label>Nom (Français)</Label>
          <Input value={form.name_french} onChange={e => setForm({...form, name_french: e.target.value})} className="bg-surface border-border" />
        </div>
        <div>
          <Label>Surnom</Label>
          <Input value={form.nickname} onChange={e => setForm({...form, nickname: e.target.value})} placeholder="ex. As-Siddiq" className="bg-surface border-border" />
        </div>
        <div>
          <Label>Année de naissance</Label>
          <Input value={form.birth_year} onChange={e => setForm({...form, birth_year: e.target.value})} placeholder="ex. 573 EC" className="bg-surface border-border" />
        </div>
        <div>
          <Label>Année de décès</Label>
          <Input value={form.death_year} onChange={e => setForm({...form, death_year: e.target.value})} placeholder="ex. 634 EC" className="bg-surface border-border" />
        </div>
      </div>
      <div>
        <Label>Relation avec le Prophète ﷺ *</Label>
        <Input value={form.relationship_to_prophet} onChange={e => setForm({...form, relationship_to_prophet: e.target.value})} required className="bg-surface border-border" />
      </div>
      <div>
        <Label>Biographie *</Label>
        <RichTextEditor
          testid="companion-biography-editor"
          value={form.biography}
          onChange={(html) => setForm({ ...form, biography: html })}
          placeholder="Saisissez ou collez la biographie du compagnon…"
        />
      </div>
      <div>
        <Label>Réalisations notables (une par ligne)</Label>
        <Textarea value={form.notable_achievements} onChange={e => setForm({...form, notable_achievements: e.target.value})} rows={3} placeholder="Premier Calife&#10;A accompagné le Prophète lors de l'Hégire" className="bg-surface border-border" />
      </div>
      <div className="flex gap-2 justify-end">
        <Button type="button" variant="outline" onClick={onCancel} className="border-border">Annuler</Button>
        <Button type="submit" disabled={loading} className="bg-primary text-background">
          {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          Enregistrer le compagnon
        </Button>
      </div>
    </form>
  );
};

// Prophet Form Component
const ProphetForm = ({ onSuccess, onCancel, initial = null }) => {
  const [loading, setLoading] = useState(false);
  const isEdit = !!initial;
  const [form, setForm] = useState(() => ({
    name_arabic: initial?.name_arabic || '',
    name_english: initial?.name_english || '',
    name_french: initial?.name_french || '',
    order: initial?.order ?? 1,
    era: initial?.era || '',
    location: initial?.location || '',
    summary: initial?.summary || '',
    quran_mentions: Array.isArray(initial?.quran_mentions)
      ? initial.quran_mentions.join(', ')
      : (initial?.quran_mentions || ''),
    certainty_level: initial?.certainty_level || 'established',
  }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = {
        ...form,
        order: parseInt(form.order),
        quran_mentions: form.quran_mentions.split(',').map(m => m.trim()).filter(m => m)
      };
      if (isEdit) {
        await adminApi.updateProphet(initial.id || initial._id, data);
        toast.success('Prophète mis à jour');
      } else {
        await adminApi.createProphet(data);
        toast.success('Prophète créé avec succès');
      }
      onSuccess?.();
    } catch (error) {
      toast.error(isEdit ? 'Échec de la mise à jour' : 'Échec de la création');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label>Nom (Anglais) *</Label>
          <Input value={form.name_english} onChange={e => setForm({...form, name_english: e.target.value})} required className="bg-surface border-border" />
        </div>
        <div>
          <Label>Nom (Arabe) *</Label>
          <Input value={form.name_arabic} onChange={e => setForm({...form, name_arabic: e.target.value})} required className="bg-surface border-border" dir="rtl" />
        </div>
        <div>
          <Label>Nom (Français)</Label>
          <Input value={form.name_french} onChange={e => setForm({...form, name_french: e.target.value})} className="bg-surface border-border" />
        </div>
        <div>
          <Label>Ordre *</Label>
          <Input type="number" value={form.order} onChange={e => setForm({...form, order: e.target.value})} required min="1" className="bg-surface border-border" />
        </div>
        <div>
          <Label>Époque</Label>
          <Input value={form.era} onChange={e => setForm({...form, era: e.target.value})} placeholder="ex. Époque patriarcale" className="bg-surface border-border" />
        </div>
        <div>
          <Label>Lieu</Label>
          <Input value={form.location} onChange={e => setForm({...form, location: e.target.value})} className="bg-surface border-border" />
        </div>
        <div>
          <Label>Niveau de certitude</Label>
          <Select value={form.certainty_level} onValueChange={v => setForm({...form, certainty_level: v})}>
            <SelectTrigger className="bg-surface border-border"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-surface border-border">
              {certaintyLevels.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Références coraniques (séparées par virgules)</Label>
          <Input value={form.quran_mentions} onChange={e => setForm({...form, quran_mentions: e.target.value})} placeholder="2:30, 2:31, 2:34" className="bg-surface border-border" />
        </div>
      </div>
      <div>
        <Label>Résumé / Biographie *</Label>
        <RichTextEditor
          testid="prophet-summary-editor"
          value={form.summary}
          onChange={(html) => setForm({ ...form, summary: html })}
          placeholder="Saisissez ou collez le résumé du prophète…"
        />
      </div>
      <div className="flex gap-2 justify-end">
        <Button type="button" variant="outline" onClick={onCancel} className="border-border">Annuler</Button>
        <Button type="submit" disabled={loading} className="bg-primary text-background" data-testid="prophet-form-save-btn">
          {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          {isEdit ? 'Mettre à jour' : 'Enregistrer le prophète'}
        </Button>
      </div>
    </form>
  );
};

export { ProphetForm };

// Tafsir Form Component
const TafsirForm = ({ onSuccess, onCancel }) => {
  const [loading, setLoading] = useState(false);
  const [surahs, setSurahs] = useState([]);
  const [verses, setVerses] = useState([]);
  const [selectedSurah, setSelectedSurah] = useState('');
  const [form, setForm] = useState({
    verse_id: '', source_name: '', author: '', content: '', language: 'arabic', is_summary: true
  });

  useEffect(() => {
    quranApi.getSurahs().then(res => setSurahs(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (selectedSurah) {
      quranApi.getVerses(parseInt(selectedSurah)).then(res => setVerses(res.data)).catch(() => {});
    }
  }, [selectedSurah]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await adminApi.createTafsir(form);
      toast.success('Tafsir créé avec succès');
      onSuccess?.();
    } catch (error) {
      toast.error('Échec de la création');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label>Choisir la sourate</Label>
          <Select value={selectedSurah} onValueChange={setSelectedSurah}>
            <SelectTrigger className="bg-surface border-border"><SelectValue placeholder="Choisir une sourate" /></SelectTrigger>
            <SelectContent className="bg-surface border-border max-h-60">
              {surahs.map(s => <SelectItem key={s.number} value={String(s.number)}>{s.number}. {s.name_french || s.name_english}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Choisir le verset *</Label>
          <Select value={form.verse_id} onValueChange={v => setForm({...form, verse_id: v})} disabled={!selectedSurah}>
            <SelectTrigger className="bg-surface border-border"><SelectValue placeholder="Choisir un verset" /></SelectTrigger>
            <SelectContent className="bg-surface border-border max-h-60">
              {verses.map(v => <SelectItem key={v.id || `${v.surah_number}:${v.verse_number}`} value={v.id || `${v.surah_number}:${v.verse_number}`}>Verset {v.verse_number}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Nom de la source *</Label>
          <Input value={form.source_name} onChange={e => setForm({...form, source_name: e.target.value})} required placeholder="ex. Tafsir Ibn Kathir" className="bg-surface border-border" />
        </div>
        <div>
          <Label>Auteur *</Label>
          <Input value={form.author} onChange={e => setForm({...form, author: e.target.value})} required placeholder="ex. Ibn Kathir" className="bg-surface border-border" />
        </div>
      </div>
      <div>
        <Label>Contenu du tafsir *</Label>
        <Textarea value={form.content} onChange={e => setForm({...form, content: e.target.value})} required rows={6} className="bg-surface border-border" />
      </div>
      <div className="flex gap-2 justify-end">
        <Button type="button" variant="outline" onClick={onCancel} className="border-border">Annuler</Button>
        <Button type="submit" disabled={loading} className="bg-primary text-background">
          {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          Enregistrer le tafsir
        </Button>
      </div>
    </form>
  );
};

const AdminDashboard = () => {
  const { user, loading: authLoading } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeForm, setActiveForm] = useState(null);

  const fetchStats = async () => {
    try {
      const response = await adminApi.getStats();
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchStats();
    }
  }, [user]);

  const handleFormSuccess = () => {
    setActiveForm(null);
    fetchStats();
  };

  if (authLoading) return <PageLoading />;
  if (!user || user.role !== 'admin') return <Navigate to="/login" replace />;

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-serif text-text-primary">Tableau de bord</h1>
              <p className="text-text-secondary mt-1">Gérer le contenu et voir les statistiques</p>
            </div>
            <div className="flex gap-2">
              <Link to="/admin/links">
                <Button variant="outline" className="border-primary/40 text-primary" data-testid="admin-link-to-links">
                  Graphe sémantique — revue
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-4 mb-8">
          {statCards.map((stat, index) => (
            <motion.div key={stat.key} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }}>
              <GlassCard className="text-center p-4">
                <stat.icon className={`w-6 h-6 mx-auto mb-2 ${stat.color}`} />
                <p className="text-2xl font-serif text-text-primary">{loading ? '...' : stats?.[stat.key] || 0}</p>
                <p className="text-xs text-text-muted">{stat.label}</p>
              </GlassCard>
            </motion.div>
          ))}
        </div>

        {/* Active Form Modal */}
        {activeForm && (
          <GlassCard className="mb-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-serif text-xl text-text-primary">
                {activeForm === 'event' && 'Ajouter un événement historique'}
                {activeForm === 'companion' && 'Ajouter un compagnon'}
                {activeForm === 'prophet' && 'Ajouter un prophète'}
                {activeForm === 'tafsir' && 'Ajouter une entrée de tafsir'}
              </h2>
              <Button variant="ghost" size="icon" onClick={() => setActiveForm(null)}><X className="w-5 h-5" /></Button>
            </div>
            {activeForm === 'event' && <EventForm onSuccess={handleFormSuccess} onCancel={() => setActiveForm(null)} />}
            {activeForm === 'companion' && <CompanionForm onSuccess={handleFormSuccess} onCancel={() => setActiveForm(null)} />}
            {activeForm === 'prophet' && <ProphetForm onSuccess={handleFormSuccess} onCancel={() => setActiveForm(null)} />}
            {activeForm === 'tafsir' && <TafsirForm onSuccess={handleFormSuccess} onCancel={() => setActiveForm(null)} />}
          </GlassCard>
        )}

        {/* Management Tabs */}
        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="bg-surface border border-border">
            <TabsTrigger value="overview" className="data-[state=active]:bg-primary/20 data-[state=active]:text-primary"><BarChart3 className="w-4 h-4 mr-2" />Aperçu</TabsTrigger>
            <TabsTrigger value="content" className="data-[state=active]:bg-primary/20 data-[state=active]:text-primary"><Database className="w-4 h-4 mr-2" />Contenu</TabsTrigger>
            <TabsTrigger value="tools" data-testid="admin-tab-tools" className="data-[state=active]:bg-primary/20 data-[state=active]:text-primary"><Settings className="w-4 h-4 mr-2" />Outils</TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-6">
              <GlassCard>
                <h3 className="font-serif text-lg text-text-primary mb-4">Actions rapides</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Button variant="outline" onClick={() => setActiveForm('event')} className="justify-start border-border text-text-secondary h-auto py-3"><Plus className="w-4 h-4 mr-2" />Ajouter un événement</Button>
                  <Button variant="outline" onClick={() => setActiveForm('companion')} className="justify-start border-border text-text-secondary h-auto py-3"><Plus className="w-4 h-4 mr-2" />Ajouter un compagnon</Button>
                  <Button variant="outline" onClick={() => setActiveForm('prophet')} className="justify-start border-border text-text-secondary h-auto py-3"><Plus className="w-4 h-4 mr-2" />Ajouter un prophète</Button>
                  <Button variant="outline" onClick={() => setActiveForm('tafsir')} className="justify-start border-border text-text-secondary h-auto py-3"><Plus className="w-4 h-4 mr-2" />Ajouter un tafsir</Button>
                </div>
              </GlassCard>
              <GlassCard>
                <h3 className="font-serif text-lg text-text-primary mb-4">État du contenu</h3>
                <div className="space-y-4">
                  <div><div className="flex justify-between mb-1"><span className="text-text-secondary text-sm">Sourates</span><span className="text-primary text-sm">{stats?.surahs || 0}/114</span></div><div className="w-full bg-surface-elevated rounded-full h-2"><div className="bg-primary h-2 rounded-full" style={{ width: `${((stats?.surahs || 0) / 114) * 100}%` }} /></div></div>
                  <div><div className="flex justify-between mb-1"><span className="text-text-secondary text-sm">Versets</span><span className="text-primary text-sm">{stats?.verses || 0}/6 236</span></div><div className="w-full bg-surface-elevated rounded-full h-2"><div className="bg-primary h-2 rounded-full" style={{ width: `${((stats?.verses || 0) / 6236) * 100}%` }} /></div></div>
                  <div><div className="flex justify-between mb-1"><span className="text-text-secondary text-sm">Prophètes</span><span className="text-primary text-sm">{stats?.prophets || 0}/25</span></div><div className="w-full bg-surface-elevated rounded-full h-2"><div className="bg-primary h-2 rounded-full" style={{ width: `${((stats?.prophets || 0) / 25) * 100}%` }} /></div></div>
                </div>
              </GlassCard>
            </div>
          </TabsContent>

          <TabsContent value="content">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <GlassCard className="hover:border-primary/30 transition-all cursor-pointer" onClick={() => setActiveForm('event')}>
                <History className="w-8 h-8 text-cyan-400 mb-4" />
                <h3 className="font-serif text-lg text-text-primary mb-2">Événements ({stats?.events || 0})</h3>
                <p className="text-sm text-text-secondary mb-4">Événements historiques et chronologie</p>
                <Button size="sm" className="bg-primary text-background"><Plus className="w-4 h-4 mr-1" />Ajouter</Button>
              </GlassCard>
              <GlassCard className="hover:border-primary/30 transition-all cursor-pointer" onClick={() => setActiveForm('companion')}>
                <Users className="w-8 h-8 text-blue-400 mb-4" />
                <h3 className="font-serif text-lg text-text-primary mb-2">Compagnons ({stats?.companions || 0})</h3>
                <p className="text-sm text-text-secondary mb-4">Profils et biographies des compagnons</p>
                <Button size="sm" className="bg-primary text-background"><Plus className="w-4 h-4 mr-1" />Ajouter</Button>
              </GlassCard>
              <GlassCard className="hover:border-primary/30 transition-all">
                <Scroll className="w-8 h-8 text-purple-400 mb-4" />
                <h3 className="font-serif text-lg text-text-primary mb-2">Prophètes ({stats?.prophets || 0})</h3>
                <p className="text-sm text-text-secondary mb-4">Profils et chronologie des prophètes</p>
                <div className="flex gap-2">
                  <Button size="sm" className="bg-primary text-background" onClick={() => setActiveForm('prophet')} data-testid="admin-prophet-add-shortcut"><Plus className="w-4 h-4 mr-1" />Ajouter</Button>
                  <Link to="/admin/prophets"><Button size="sm" variant="outline" className="border-primary/40 text-primary" data-testid="admin-prophet-manage-link"><Pencil className="w-4 h-4 mr-1" />Gérer</Button></Link>
                </div>
              </GlassCard>
              <GlassCard className="hover:border-primary/30 transition-all cursor-pointer" onClick={() => setActiveForm('tafsir')}>
                <BookOpen className="w-8 h-8 text-emerald-400 mb-4" />
                <h3 className="font-serif text-lg text-text-primary mb-2">Tafsir</h3>
                <p className="text-sm text-text-secondary mb-4">Commentaires et explications des versets</p>
                <Button size="sm" className="bg-primary text-background"><Plus className="w-4 h-4 mr-1" />Ajouter</Button>
              </GlassCard>
            </div>
          </TabsContent>

          <TabsContent value="tools" data-testid="admin-tools-content">
            <div className="space-y-6">
              <MorphologyToolsCard />
              <GlossesToolsCard />
              <LexicalImportCard />
            </div>
          </TabsContent>
        </Tabs>

        <GlassCard className="mt-8 border-primary/30">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0"><FileText className="w-5 h-5 text-primary" /></div>
            <div>
              <h4 className="font-medium text-text-primary mb-1">Contenu initial</h4>
              <p className="text-sm text-text-secondary">Le Coran complet (114 sourates, 6 236 versets, traductions FR/EN) est déjà chargé. Utilisez les formulaires ci-dessus pour ajouter du contenu savant vérifié (tafsir, hadiths, événements).</p>
            </div>
          </div>
        </GlassCard>
      </div>
    </div>
  );
};

export default AdminDashboard;
